import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { format, parse, startOfWeek, getDay, addMonths, subMonths } from "date-fns";
import esES from "date-fns/locale/es";
import { Global } from "../helpers/Global";
import Select from "react-select";

const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) => format(date, formatStr, { ...options, locale: esES }),
  parse: (value, formatStr, options) => parse(value, formatStr, new Date(), { ...options, locale: esES }),
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1, locale: esES }),
  getDay,
  locales: { es: esES },
});

const CustomEvent = ({ event }) => (
  <div style={{ fontWeight: 600 }}>
    {event.usuario ? `[${event.usuario}] ` : ""}
    {event.title}
    {event.descripcion && (
      <div style={{ fontSize: "0.85em", fontWeight: 400 }}>{event.descripcion}</div>
    )}
  </div>
);

function normalize(text) {
  return (text || "")
    .toString()
    .lowerCase?.() // por si llega algo raro
    ?.normalize?.("NFD")
    ?.replace?.(/[\u0300-\u036f]/g, "") || (text || "").toString().toLowerCase();
}

export const CalendarioGlobal = () => {
  const calendarRef = useRef();
  const [plantillas, setPlantillas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [todosLosTurnos, setTodosLosTurnos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [vistaActual, setVistaActual] = useState("week");
  const [rangoVisible, setRangoVisible] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);

  // ---- FILTROS ----
  const [filtroUsuarios, setFiltroUsuarios] = useState([]);      // array de ids (number)
  const [filtroPlantillas, setFiltroPlantillas] = useState([]);  // array de ids (number o -1 para "sin plantilla")
  const [filtroTipos, setFiltroTipos] = useState(new Set(["turno", "ausencia"]));
  const [mostrarInactivos, setMostrarInactivos] = useState(true);
  const [busqueda, setBusqueda] = useState("");

  // ---- Portal props para todos los Select ----
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  const selectPortalStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }),
    []
  );

  const hoy = new Date();
  const [rangoCargado, setRangoCargado] = useState(() => {
    const desde = subMonths(new Date(hoy.getFullYear(), hoy.getMonth(), 1), 3);
    const hasta = addMonths(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0), 4);
    return { desde, hasta };
  });

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [resPlantillas, resUsuarios] = await Promise.all([
          fetch(Global.url + "turnos/plantillas", {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(Global.url + "usuarios", {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);

        const dataPlantillas = resPlantillas.ok ? await resPlantillas.json() : [];
        const dataUsuarios = resUsuarios.ok ? await resUsuarios.json() : [];

        setPlantillas(dataPlantillas);
        setUsuarios(dataUsuarios);
      } catch {
        setMensaje("Error al cargar plantillas o usuarios");
      }
    };

    cargarDatos();
  }, []);

  const fetchTurnosGlobales = (desde, hasta) => {
    setLoading(true);
    fetch(
      `${Global.url}turnos/global?desde=${desde.toISOString().slice(0, 10)}&hasta=${hasta
        .toISOString()
        .slice(0, 10)}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        const turnos = (data || []).map((turno) => {
          const usuario = usuarios.find((u) => u.id === turno.usuario_id);
          const nombreCompleto = usuario
            ? `${usuario.nombre} ${usuario.apellidos || ""}`
            : `ID ${turno.usuario_id}`;

          return {
            id: turno.id,
            title: turno.titulo || "Turno",
            start: new Date(turno.inicio.replace(" ", "T")),
            end: new Date(turno.fin.replace(" ", "T")),
            allDay: false,
            activo: turno.activo,
            tipo: turno.tipo, // "turno" | "ausencia"
            descripcion: turno.descripcion,
            plantilla_id: turno.plantilla_id, // puede ser null
            usuario: nombreCompleto,
            usuario_id: turno.usuario_id,
          };
        });

        setTodosLosTurnos(turnos);
        setRangoCargado({ desde, hasta });
      })
      .catch(() => setMensaje("Error al cargar turnos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    setRangoVisible({ desde, hasta });
    fetchTurnosGlobales(rangoCargado.desde, rangoCargado.hasta);
    // eslint-disable-next-line
  }, [plantillas, usuarios]);

  useEffect(() => {
    if (vistaActual === "day") {
      const visibleDate = calendarRef.current?.state?.currentDate || new Date();
      const desde = new Date(visibleDate);
      const hasta = new Date(visibleDate);
      hasta.setHours(23, 59, 59, 999);
      setRangoVisible({ desde, hasta });
    }
  }, [vistaActual]);

  // ---- Opciones de Selects ----
  const usuarioOptions = useMemo(
    () =>
      usuarios
        .slice()
        .sort((a, b) => (a.nombre + (a.apellidos || "")).localeCompare(b.nombre + (b.apellidos || "")))
        .map((u) => ({ value: Number(u.id), label: `${u.nombre} ${u.apellidos || ""}`.trim() })),
    [usuarios]
  );

  const plantillaOptions = useMemo(() => {
    const base = plantillas
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map((p) => ({ value: Number(p.id), label: p.nombre || `Plantilla ${p.id}` }));

    // Opción para "sin plantilla" si hay eventos sin plantilla_id
    const haySinPlantilla = todosLosTurnos.some((t) => !t.plantilla_id);
    return haySinPlantilla ? [{ value: -1, label: "Sin plantilla" }, ...base] : base;
  }, [plantillas, todosLosTurnos]);

  // ---- Filtro + rango visible -> eventos ----
  useLayoutEffect(() => {
    if (!rangoVisible) return;

    const usuariosSel = new Set(filtroUsuarios);
    const plantillasSelRaw = new Set(filtroPlantillas);
    const plantillasSel = new Set(
      [...plantillasSelRaw].map((v) => (v === -1 ? null : v)) // -1 representa "sin plantilla"
    );
    const tiposSel = filtroTipos;
    const q = normalize(busqueda);

    const filtrados = todosLosTurnos.filter((evt) => {
      // Rango visible
      if (evt.start > rangoVisible.hasta || evt.end < rangoVisible.desde) return false;

      // Activo
      if (!mostrarInactivos && !evt.activo) return false;

      // Usuario
      if (usuariosSel.size > 0 && !usuariosSel.has(Number(evt.usuario_id))) return false;

      // Plantilla (permite null -> opción -1)
      if (plantillasSel.size > 0 && !plantillasSel.has(evt.plantilla_id ?? null)) return false;

      // Tipo
      if (!tiposSel.has(evt.tipo)) return false;

      // Búsqueda texto: título + descripción + usuario
      if (q) {
        const hay =
          normalize(evt.title).includes(q) ||
          normalize(evt.descripcion).includes(q) ||
          normalize(evt.usuario).includes(q);
        if (!hay) return false;
      }

      return true;
    });

    setEventos(filtrados);
  }, [
    rangoVisible,
    todosLosTurnos,
    filtroUsuarios,
    filtroPlantillas,
    filtroTipos,
    mostrarInactivos,
    busqueda,
  ]);

  const handleRangeChange = (range) => {
    let desde, hasta;

    if (Array.isArray(range)) {
      desde = range[0];
      hasta = range[range.length - 1];
    } else if (range.start && range.end) {
      desde = range.start;
      hasta = range.end;
    } else if (range instanceof Date) {
      desde = new Date(range);
      hasta = new Date(range);
      hasta.setHours(23, 59, 59, 999);
    } else {
      const hoy = new Date();
      desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    }

    setRangoVisible({ desde, hasta });

    if (desde < rangoCargado.desde || hasta > rangoCargado.hasta) {
      const nuevoCentro = new Date(desde.getFullYear(), desde.getMonth(), 1);
      const nuevoDesde = subMonths(nuevoCentro, 3);
      const nuevoHasta = addMonths(new Date(nuevoCentro.getFullYear(), nuevoCentro.getMonth() + 1, 0), 4);
      fetchTurnosGlobales(nuevoDesde, nuevoHasta);
    }
  };

  const eventPropGetter = (event) => {
    let estilo = {};
    if (event.plantilla_id) {
      const plantilla = plantillas.find((p) => String(p.id) === String(event.plantilla_id));
      if (plantilla && plantilla.color) {
        estilo.backgroundColor = plantilla.color;
        estilo.color = "#222";
      }
    }
    if (event.tipo === "ausencia") {
      estilo.backgroundColor = "#ffbaba";
      estilo.color = "#fff";
    }
    if (!event.activo) {
      estilo.backgroundColor = "#ddd";
      estilo.color = "#888";
      estilo.textDecoration = "line-through";
    }
    if (!estilo.backgroundColor) {
      estilo.backgroundColor = "#85C1E9";
      estilo.color = "#222";
    }
    return { style: estilo };
  };

  const toggleTipo = (tipo) => {
    setFiltroTipos((prev) => {
      const next = new Set(prev);
      if (next.has(tipo)) next.delete(tipo);
      else next.add(tipo);
      return next;
    });
  };

  const limpiarFiltros = () => {
    setFiltroUsuarios([]);
    setFiltroPlantillas([]);
    setFiltroTipos(new Set(["turno", "ausencia"]));
    setMostrarInactivos(true);
    setBusqueda("");
  };

  return (
    <div className="cal-wrapper">
      <h2 className="centrar-titulo">Vista global de turnos</h2>
      {mensaje && <p className="rojo">{mensaje}</p>}

      {/* Filtros (Selects portalados al body) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          gap: 12,
          marginBottom: 12,
          alignItems: "end",
        }}
      >
        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Usuarios</label>
          <Select
            isMulti
            options={usuarioOptions}
            value={usuarioOptions.filter((opt) => filtroUsuarios.includes(opt.value))}
            onChange={(vals) => setFiltroUsuarios(vals.map((v) => Number(v.value)))}
            placeholder="Filtrar por usuarios"
            classNamePrefix="select"
            menuPortalTarget={portalTarget}
            styles={selectPortalStyles}
          />
        </div>

        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Plantillas</label>
          <Select
            isMulti
            options={plantillaOptions}
            value={plantillaOptions.filter((opt) => filtroPlantillas.includes(opt.value))}
            onChange={(vals) => setFiltroPlantillas(vals.map((v) => Number(v.value)))}
            placeholder="Filtrar por plantillas"
            classNamePrefix="select"
            menuPortalTarget={portalTarget}
            styles={selectPortalStyles}
          />
        </div>

        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Búsqueda</label>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Título, descripción o usuario…"
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div title="Mostrar turnos activos e inactivos">
            <label style={{ fontWeight: 600, display: "block" }}>Inactivos</label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={mostrarInactivos}
                onChange={(e) => setMostrarInactivos(e.target.checked)}
              />
              Mostrar
            </label>
          </div>

          <div>
            <label style={{ fontWeight: 600, display: "block" }}>Tipo</label>
            <div style={{ display: "flex", gap: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={filtroTipos.has("turno")}
                  onChange={() => toggleTipo("turno")}
                />
                Turno
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={filtroTipos.has("ausencia")}
                  onChange={() => toggleTipo("ausencia")}
                />
                Ausencia
              </label>
            </div>
          </div>

          <div style={{ marginLeft: "auto" }}>
            <button className="btn-login" onClick={limpiarFiltros}>Limpiar</button>
          </div>
        </div>
      </div>

      <Calendar
        ref={calendarRef}
        localizer={localizer}
        events={eventos}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        defaultView="week"
        views={["day", "week", "month", "agenda"]}
        onView={setVistaActual}
        onRangeChange={handleRangeChange}
        /* 👉 Rango vertical y autoscroll */
        scrollToTime={new Date(1970, 0, 1, 7, 30)}
        min={new Date(1970, 0, 1, 0, 0)}
        max={new Date(1970, 0, 1, 23, 59, 59)}
        step={15}
        timeslots={4}
        defaultDate={new Date()}
        style={{ height: "100%" }}
        eventPropGetter={eventPropGetter}
        components={{ event: CustomEvent }}
        messages={{
          today: "Hoy",
          previous: "Atrás",
          next: "Siguiente",
          month: "Mes",
          week: "Semana",
          day: "Día",
          agenda: "Agenda",
          date: "Fecha",
          time: "Hora",
          event: "Evento",
          noEventsInRange: "No hay eventos en este rango.",
          showMore: (total) => `+ Ver más (${total})`,
        }}
      />

      {loading && <p>Cargando turnos...</p>}
    </div>
  );
};
